/**
 * Theme preference: 'light' | 'dark' | 'system'. Stored choice wins; 'system'
 * (or no stored value) defers to prefers-color-scheme via CSS — we only stamp
 * data-theme for an explicit choice. Pure functions; storage/DOM injected.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ur-theme';

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function readStoredTheme(storage: Pick<Storage, 'getItem'>): ThemeChoice {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return isThemeChoice(raw) ? raw : 'system';
  } catch {
    return 'system'; // storage blocked (private mode) — defer to system
  }
}

/** Returns the data-theme attribute value to stamp, or null to remove it. */
export function themeAttributeFor(choice: ThemeChoice): 'light' | 'dark' | null {
  return choice === 'system' ? null : choice;
}

export function applyTheme(
  choice: ThemeChoice,
  root: { setAttribute(n: string, v: string): void; removeAttribute(n: string): void },
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
): void {
  const attr = themeAttributeFor(choice);
  if (attr === null) root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', attr);
  try {
    if (choice === 'system') storage.removeItem(STORAGE_KEY);
    else storage.setItem(STORAGE_KEY, choice);
  } catch {
    // storage blocked — theme still applies for this page view
  }
}
